import React from 'react';
import '../dashboard.css';
import { formatCount } from '../overview/format';
import { getChartColor } from '../../../config/statusColors';

export interface StatusBarItem {
  key: string;
  label: string;
  count: number;
  /** Any CSS color, typically a var(). */
  color: string;
}

interface StatusBarProps {
  items: StatusBarItem[];
  /** Keep zero-count entries in the legend (the bar always skips them). */
  showZero?: boolean;
}

/**
 * Status distribution as one stacked bar plus a legend. Replaces the
 * donut charts: the same composition in a sliver of the space, and it
 * stays readable when one status holds nearly everything.
 */
const StatusBar: React.FC<StatusBarProps> = ({ items, showZero = false }) => {
  const total = items.reduce((sum, i) => sum + (i.count || 0), 0);
  if (total === 0) return null;

  const legend = showZero ? items : items.filter((i) => i.count > 0);

  return (
    <div className="dash-dist">
      <div className="dash-dist-bar" role="img" aria-label={legend.map((i) => `${i.label} ${i.count}`).join(', ')}>
        {items.filter((i) => i.count > 0).map((i) => (
          <span key={i.key} style={{ width: `${(i.count / total) * 100}%`, background: i.color }} />
        ))}
      </div>
      <div className="dash-legend">
        {legend.map((i) => (
          <div key={i.key} className="dash-legend-item">
            <span className="dash-swatch" style={{ background: i.color }} />
            <span>{i.label}</span>
            <b>{formatCount(i.count)}</b>
          </div>
        ))}
      </div>
    </div>
  );
};

/** status -> count map to bar items in the canonical status palette (map order kept). */
export const statusBarItems = (
  counts: Record<string, number | undefined>,
  labels: Record<string, string> = {}
): StatusBarItem[] =>
  Object.entries(counts).map(([key, count]) => ({
    key,
    label: labels[key] || key,
    count: count ?? 0,
    color: getChartColor(key),
  }));

export default StatusBar;
